import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { FocusOn } from 'react-focus-on';
import { useAppContext } from '@provider/app-context';
import { isPlatformNodeJS } from '@utils/common';
import { NoSSR, CSSRefTransition } from '@utils/react';
import { BUTTON_TRANSITION_DURATION_MS, bubbleCls, buttonCls, infoPoppercls, infoCls, infoHeadingCls, infoCloseButtonCls } from './guide-bubble.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { Overlay } from '../overlays/overlay/overlay.js';

const LOCAL_STORAGE_PREFIX = "pm2:skin__guideBubble:dismissed";
const ENABLED_PORTAL_IDS = ["88c2d1e8-962e-47b5-a415-7ae3e3661f80"];
function GuideBubble({
  id,
  className,
  popperPlacement,
  enableForAllPortals,
  popperOffsetPx,
  hasUserInteracted,
  label,
  title,
  children
}) {
  const productId = useAppContext(state => state.product?.id ?? "");
  const buttonRef = useRef(null);
  const infoContainerRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => determineDisabledState(id));
  const [toggled, setToggled] = useState(false);
  const disabled = hasUserInteracted || dismissed;
  useEffect(() => {
    // register when a bubble has been disabled in local storage, so that it
    // doesn't re-activate the next time the user visits the website
    if (disabled) {
      try {
        window.localStorage.setItem(`${LOCAL_STORAGE_PREFIX}__${id}`, "true");
      } catch (err) {
        console.error(err);
      }
      setDismissed(true);
    }
  }, [id, disabled]);
  if (!enableForAllPortals && !ENABLED_PORTAL_IDS.includes(productId)) {
    return null;
  }
  const handleBubbleClick = () => {
    if (!toggled && !dismissed) {
      setToggled(true);
    } else if (!dismissed) {
      setToggled(false);
      setDismissed(true);
    }
  };
  const handleCloseClick = () => {
    setToggled(false);
    setDismissed(true);
  };
  return /*#__PURE__*/jsxs(NoSSR, {
    children: [/*#__PURE__*/jsx(CSSRefTransition, {
      in: !disabled,
      timeout: {
        enter: 0,
        exit: BUTTON_TRANSITION_DURATION_MS
      },
      mountOnEnter: true,
      unmountOnExit: true,
      nodeRef: buttonRef,
      children: ref => /*#__PURE__*/jsx("button", {
        ref: ref,
        className: cx(buttonCls, className),
        "aria-label": label,
        onClick: handleBubbleClick,
        children: /*#__PURE__*/jsx("span", {
          className: bubbleCls
        })
      })
    }), /*#__PURE__*/jsx(Overlay, {
      active: toggled,
      className: infoPoppercls,
      containerRef: buttonRef,
      placement: popperPlacement,
      offsetPx: 12 + (popperOffsetPx ?? 5),
      marginPx: 40,
      children: /*#__PURE__*/jsx(FocusOn, {
        enabled: toggled,
        onEscapeKey: handleCloseClick,
        onClickOutside: handleCloseClick,
        scrollLock: false,
        focusLock: true,
        autoFocus: true,
        noIsolation: true,
        allowPinchZoom: true,
        returnFocus: {
          preventScroll: true
        },
        preventScrollOnFocus: true,
        children: /*#__PURE__*/jsxs("div", {
          ref: infoContainerRef,
          className: infoCls,
          tabIndex: 0,
          children: [/*#__PURE__*/jsx("h4", {
            className: infoHeadingCls,
            children: title
          }), children, /*#__PURE__*/jsx("button", {
            className: infoCloseButtonCls,
            onClick: handleCloseClick,
            "aria-label": "Luk panelet",
            children: /*#__PURE__*/jsx("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 21.584 16.686",
              height: "15",
              style: {
                marginTop: "2px"
              },
              children: /*#__PURE__*/jsx("path", {
                fill: "none",
                stroke: "currentColor",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                strokeWidth: "3",
                d: "M19.485 2.1l-9.107 12.144a2.357 2.357 0 01-3.545.254l-4.711-4.71"
              })
            })
          })]
        })
      })
    })]
  });
}
function determineDisabledState(id) {
  try {
    return isPlatformNodeJS() ? false : window.localStorage.getItem(`${LOCAL_STORAGE_PREFIX}__${id}`) === "true";
  } catch (err) {
    console.error(err);
    return false;
  }
}

export { GuideBubble };
//# sourceMappingURL=guide-bubble.js.map
